import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { User } from '@supabase/supabase-js';
import { authService } from '../services/auth';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { LoginModal } from '../components/LoginModal';

export const About = () => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = authService.onAuthStateChange((currentUser) => {
      setUser(currentUser);
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleSignOut = async () => {
    try {
      await authService.signOut();
      setUser(null);
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  if (isLoading) {
    return null;
  }

  return (
    <>
      <Header
        user={user}
        onLoginClick={() => setIsLoginModalOpen(true)}
        onSignOut={handleSignOut}
      />

      <LoginModal
        isOpen={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
        onLoginSuccess={() => {
          setIsLoginModalOpen(false);
        }}
      />

      <main className="page-main">
        {/* Hero Section */}
        <section className="page-hero">
          <div className="page-hero-inner">
            <div className="page-badge">About Pinak</div>
            <h1>Building the future of<br />AI-powered CAD design</h1>
            <p className="page-hero-description">
              We're on a mission to democratize professional CAD design. No specialized training required—just describe what you need, and Pinak brings it to life.
            </p>
          </div>
        </section>

        {/* Stats Section */}
        <section className="about-stats-section">
          <div className="about-stats-container">
            <div className="about-stats-grid">
              <div className="about-stat-card">
                <span className="about-stat-number">10x</span>
                <span className="about-stat-label">Faster Design</span>
              </div>
              <div className="about-stat-card">
                <span className="about-stat-number">100%</span>
                <span className="about-stat-label">Production Ready</span>
              </div>
              <div className="about-stat-card">
                <span className="about-stat-number">0</span>
                <span className="about-stat-label">CAD Training Needed</span>
              </div>
              <div className="about-stat-card">
                <span className="about-stat-number">∞</span>
                <span className="about-stat-label">Design Possibilities</span>
              </div>
            </div>
          </div>
        </section>

        {/* Mission Section */}
        <section className="about-mission-section">
          <div className="about-mission-container">
            <div className="section-header-left">
              <span className="section-label">Our Mission</span>
              <h2>Making world-class design accessible to everyone</h2>
            </div>
            <div className="about-mission-content">
              <p>
                PINAK exists to democratize professional CAD design. We believe that creating production-ready 3D models shouldn't require years of training or expensive software licenses. By combining cutting-edge AI with intuitive interfaces, we're making world-class design capabilities accessible to everyone—from engineers and designers to entrepreneurs and makers.
              </p>
            </div>
          </div>
        </section>

        {/* Values Grid */}
        <section className="about-values-section">
          <div className="about-values-container">
            <div className="about-values-grid">
              <div className="about-value-card">
                <div className="about-value-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <h3>Accessibility</h3>
                <p>Break down barriers to professional CAD design. No specialized training required—just describe what you need, and PINAK brings it to life.</p>
              </div>
              <div className="about-value-card">
                <div className="about-value-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M13 10V3L4 14h7v7l9-11h-7z" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <h3>Speed</h3>
                <p>Transform hours of manual modeling into seconds of AI-powered generation. Iterate faster, prototype sooner, ship products quicker.</p>
              </div>
              <div className="about-value-card">
                <div className="about-value-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <h3>Quality</h3>
                <p>Production-ready designs from day one. PINAK generates models that meet real-world manufacturing standards and integrate seamlessly with existing workflows.</p>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="about-how-section">
          <div className="about-how-container">
            <div className="section-header-center">
              <span className="section-label">Technology</span>
              <h2>How Pinak Works</h2>
            </div>
            <div className="about-how-content">
              <div className="about-how-card">
                <div className="about-how-number">01</div>
                <div className="about-how-text">
                  <h3>Understand Intent</h3>
                  <p>PINAK leverages advanced language models to understand design intent from natural language descriptions. No CAD knowledge required.</p>
                </div>
              </div>
              <div className="about-how-card">
                <div className="about-how-number">02</div>
                <div className="about-how-text">
                  <h3>Generate Geometry</h3>
                  <p>Our system applies engineering principles and computational geometry to create precise 3D models that match your specifications.</p>
                </div>
              </div>
              <div className="about-how-card">
                <div className="about-how-number">03</div>
                <div className="about-how-text">
                  <h3>Validate & Export</h3>
                  <p>Every design is validated through FreeCAD's powerful CAD kernel, ensuring geometric accuracy and manufacturing compatibility.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Core Values */}
        <section className="about-core-section">
          <div className="about-core-container">
            <div className="section-header-center">
              <span className="section-label">What We Believe</span>
              <h2>Our Core Values</h2>
            </div>
            <div className="about-core-grid">
              <div className="about-core-card">
                <h4>Innovation First</h4>
                <p>We push the boundaries of what's possible with AI and CAD, constantly exploring new ways to make design more intuitive and powerful.</p>
              </div>
              <div className="about-core-card">
                <h4>User-Centric</h4>
                <p>Every feature we build starts with understanding our users' needs. We design for clarity, simplicity, and real-world utility.</p>
              </div>
              <div className="about-core-card">
                <h4>Open & Transparent</h4>
                <p>We believe in open standards, transparent processes, and building tools that work with your existing stack—not against it.</p>
              </div>
              <div className="about-core-card">
                <h4>Quality Engineering</h4>
                <p>We don't compromise on precision. Every model generated by PINAK meets professional standards for accuracy and manufacturability.</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="page-cta-section">
          <div className="page-cta-container">
            <div className="page-cta-card">
              <h2>Ready to Transform Your Design Workflow?</h2>
              <p>Join engineers, designers, and innovators who are already using PINAK to bring their ideas to life faster than ever before.</p>
              <div className="page-cta-buttons">
                <Link to="/viewer" className="btn-primary-lg">
                  Get Started
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M5 12h14M12 5l7 7-7 7"/>
                  </svg>
                </Link>
                <a href="mailto:sales@pinak.ai" className="btn-secondary-lg">Contact Sales</a>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};
